import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Eye, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "./admin-utils";
import { OrderDetailModal } from "./OrderDetailModal";
import { OrderEditorModal } from "./OrderEditorModal";

const STATUS_STYLE = {
  pending: "bg-gray-200 text-gray-700",
  confirmed: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
};

const STATUS_OPTIONS = ["pending", "confirmed", "delivered"];

const formatDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export function AdminOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    location: "all",
    status: "all",
    q: "",
    start: "",
    end: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [editor, setEditor] = useState({ open: false, order: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadLocations = useCallback(() => {
    axios
      .get(`${API}/admin/locations`)
      .then((r) => setLocations(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const openEditor = (order) => {
    loadLocations();
    setEditor({ open: true, order });
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location !== "all") params.set("location", filters.location);
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.q) params.set("q", filters.q);
      if (filters.start) params.set("start", filters.start);
      if (filters.end) params.set("end", filters.end);
      params.set("limit", "200");
      const res = await axios.get(`${API}/admin/orders?${params}`);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
      setSelectedIds(new Set()); // reset selection on new fetch
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const visibleIds = useMemo(() => orders.map((o) => o.id), [orders]);
  const visibleSelectedCount = useMemo(
    () => visibleIds.filter((id) => selectedIds.has(id)).length,
    [visibleIds, selectedIds]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const setSingleStatus = async (order, nextStatus) => {
    if (!STATUS_OPTIONS.includes(nextStatus)) return;
    if ((order.status || "pending") === nextStatus) return;
    setStatusSavingId(order.id);
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, {
        status: nextStatus,
      });
      toast.success(`Order ${order.order_number || order.id.slice(0, 8)} → ${nextStatus}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setStatusSavingId(null);
    }
  };

  const runBulkStatus = async () => {
    if (!bulkStatus) {
      toast.error("Pick a status first");
      return;
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkSaving(true);
    try {
      const res = await axios.put(`${API}/admin/orders/bulk-status`, {
        order_ids: ids,
        status: bulkStatus,
      });
      toast.success(
        `Updated ${res.data.modified} order${res.data.modified === 1 ? "" : "s"} → ${bulkStatus}`
      );
      setBulkStatus("");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const downloadCsv = async () => {
    const params = new URLSearchParams();
    if (filters.location !== "all") params.set("location", filters.location);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);
    try {
      const res = await axios.get(`${API}/admin/orders/export.csv?${params}`, { responseType: "blob" });
      const name =
        res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || "orders.csv";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("CSV export failed");
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/orders/${deleteTarget.id}`);
      toast.success(`Order ${deleteTarget.order_number || ""} deleted`);
      setDeleteTarget(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeleting(false);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-3" data-testid="admin-orders-tab">
      <Card className="p-3">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <Select
            value={filters.location}
            onValueChange={(v) => setFilters((f) => ({ ...f, location: v }))}
          >
            <SelectTrigger data-testid="orders-filter-location">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((l) => (
                <SelectItem key={l.slug} value={l.slug}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.status}
            onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          >
            <SelectTrigger data-testid="orders-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Search order #, name, email"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className="col-span-2"
            data-testid="orders-search"
          />
          <Input
            type="date"
            value={filters.start}
            onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
            title="Delivery date — from"
            data-testid="orders-delivery-start"
          />
          <Input
            type="date"
            value={filters.end}
            onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
            title="Delivery date — to"
            data-testid="orders-delivery-end"
          />
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          Date range filters by <b>delivery date</b> (used to plan production).
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Button
            size="sm"
            onClick={() => openEditor(null)}
            className="bg-[#1aa59a] hover:bg-[#158a80]"
            data-testid="orders-new"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> New order
          </Button>
          <Button
            size="sm"
            onClick={fetchOrders}
            variant="outline"
            data-testid="orders-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={downloadCsv}
            className="bg-[#1aa59a] hover:bg-[#158a80]"
            data-testid="orders-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> CSV Export
          </Button>
          <span className="text-xs text-gray-500 ml-auto">{total} order(s)</span>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1aa59a]" />
        </div>
      ) : orders.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          No orders match this filter.
        </Card>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="p-2 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
                    data-testid="orders-select-all"
                    aria-label="Select all visible orders"
                  />
                </th>
                <th className="text-left p-2">Order #</th>
                <th className="text-left p-2">Order Date</th>
                <th className="text-left p-2">Delivery Date</th>
                <th className="text-left p-2">Location</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-right p-2">Total</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const checked = selectedIds.has(o.id);
                const rowBg = checked ? "bg-teal-50" : "hover:bg-gray-50";
                return (
                  <tr
                    key={o.id}
                    className={`border-t ${rowBg}`}
                    data-testid={`admin-order-${o.order_number}`}
                  >
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(o.id)}
                        className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
                        data-testid={`orders-select-${o.id}`}
                        aria-label={`Select ${o.order_number}`}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">
                      {o.order_number || o.id.slice(0, 8)}
                    </td>
                    <td className="p-2 text-xs whitespace-nowrap">
                      {formatDateShort(o.created_at)}
                    </td>
                    <td className="p-2 text-xs whitespace-nowrap font-medium">
                      {o.delivery_date ? formatDateShort(o.delivery_date) : "—"}
                    </td>
                    <td className="p-2 text-xs">
                      {o.location_slug || <span className="text-gray-400">guest</span>}
                    </td>
                    <td className="p-2 text-xs">
                      <div className="font-medium">{o.customer_name}</div>
                      <div className="text-gray-500">{o.customer_email}</div>
                    </td>
                    <td className="p-2 text-right font-bold text-[#1aa59a]">
                      £{Number(o.total_amount || 0).toFixed(2)}
                    </td>
                    <td className="p-2">
                      <Select
                        value={o.status || "pending"}
                        onValueChange={(v) => setSingleStatus(o, v)}
                        disabled={statusSavingId === o.id}
                      >
                        <SelectTrigger
                          className={`h-7 text-[11px] uppercase font-bold px-2 py-0 rounded-full w-[110px] ${
                            STATUS_STYLE[o.status] || STATUS_STYLE.pending
                          }`}
                          data-testid={`order-status-${o.order_number}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => setDetailOrder(o)}
                        data-testid={`order-view-${o.order_number}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs ml-1"
                        onClick={() => openEditor(o)}
                        data-testid={`order-edit-${o.order_number}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs ml-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(o)}
                        data-testid={`order-delete-${o.order_number}`}
                        aria-label="Delete order"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sticky bulk bar */}
      {selectedCount > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t bg-white shadow-2xl"
          data-testid="orders-bulk-bar"
        >
          <div className="max-w-5xl mx-auto p-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              title="Clear selection"
              data-testid="orders-bulk-clear"
            >
              <X className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-gray-800">
              {selectedCount} order{selectedCount === 1 ? "" : "s"} selected
            </span>
            <div className="flex-1" />
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="w-[180px]" data-testid="orders-bulk-status-select">
                <SelectValue placeholder="Set status to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="bg-[#1aa59a] hover:bg-[#158a80]"
              onClick={runBulkStatus}
              disabled={bulkSaving || !bulkStatus}
              data-testid="orders-bulk-apply"
            >
              {bulkSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>
        </div>
      )}

      <OrderDetailModal
        open={!!detailOrder}
        onOpenChange={(open) => (!open ? setDetailOrder(null) : null)}
        order={detailOrder}
      />

      <OrderEditorModal
        open={editor.open}
        onOpenChange={(open) => setEditor((e) => ({ ...e, open }))}
        order={editor.order}
        locations={locations}
        onSaved={fetchOrders}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="order-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order {deleteTarget?.order_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order for {deleteTarget?.customer_name}. It can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="order-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
